import assert from 'assert';

import {
  NameTemplatePartType,
  NameTemplateType,
  ProcedureType,
  StudyType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import {getProcedureTypeForStudyTypeAllowNull} from 'studies/TypedStudyUtils';

import NameTemplatePartConfiguration from './NameTemplatePartConfiguration';

/**
 * Creates a name template part configuration that includes study type specific logic.
 *
 * This function is a convenience function for creating a name template part configuration that is specific to name template part types that depend on study type.
 */
export default function createStudyTypeNameTemplatePartConfiguration(
  studyType: StudyType,
  schemaPrefix: string
): NameTemplatePartConfiguration {
  const procedureType: ProcedureType | null = getProcedureTypeForStudyTypeAllowNull(studyType);

  // This function was introduced to allow for the StudyType configuration object to be used by StudyType.CUSTOM.
  // This is necessary because StudyType.CUSTOM does not have a procedure type.
  const getProcedureString = (stringCase?: StringCase): string => {
    assert(
      procedureType != null,
      'Expected procedure type to be defined in order to compute the procedure UI string.'
    );
    return getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {stringCase});
  };

  function getDisplayString(partType: NameTemplatePartType): string | null {
    switch (partType) {
      case NameTemplatePartType.PROCEDURE_NAME:
        return `${getProcedureString()} name`;
      case NameTemplatePartType.PROCEDURE_RUN_ID:
        return `${getProcedureString()} run ID`;
      default:
        return null;
    }
  }

  function getExamplePartValue(partType: NameTemplatePartType): string | null {
    switch (partType) {
      case NameTemplatePartType.PROCEDURE_NAME:
        return `${getProcedureString()}Name`;
      case NameTemplatePartType.PROCEDURE_RUN_ID: {
        assert(
          procedureType != null,
          'Procedure type should be defined to get example part value for PROCEDURE_RUN_ID'
        );
        let exampleValue: string | null = null;

        if (procedureType === ProcedureType.RECIPE) {
          exampleValue = 'RR1';
        } else if (procedureType === ProcedureType.ASSAY) {
          exampleValue = 'AR1';
        }
        assert(exampleValue != null, `Unsupported procedure type ${procedureType} for getExamplePartValue.`);
        return exampleValue;
      }
      case NameTemplatePartType.STUDY_ID:
        return `${schemaPrefix}1`;
      default:
        return null;
    }
  }

  function getNameTemplateTypeFriendlyName(nameTemplateType: NameTemplateType): string | null {
    if (nameTemplateType === NameTemplateType.PROCEDURE_RUN) {
      return `${getProcedureString(StringCase.LOWER)} run`;
    }
    return null;
  }

  function getDisplayBannerText(nameTemplateType: NameTemplateType): string | null {
    if (nameTemplateType !== NameTemplateType.PROCEDURE_RUN || procedureType == null) {
      return null;
    }
    return `${getProcedureString()} run ID is a required name part and must be included within the ${getProcedureString(StringCase.LOWER)} run name template.`;
  }

  return {
    getDisplayString,
    getExamplePartValue,
    getNameTemplateTypeFriendlyName,
    getDisplayBannerText,
  };
}
